<template lang="pug">
main#main-content.relative.flex.w-full.items-start
  .side.ms-3.h-full(v-if="showMobileMenu", class="portrait:ms-0 portrait:mb-6 portrait:mt-0")
    SideFlat(v-if="menu", :menu="menu", :menuItemStack="menuItemStack", :historyStack="historyStack", @clickLogo="clickLogo")
  client-only
    Banner.banner.main-element.ms-3(v-if="shouldShowBanner", :list="bannerList", class="portrait:hidden", :class="{ 'navbar-exist': hasNavbar }", @showCustomerService="showCustomerService")
  .flex-1.ps-2.overflow-x-scroll.game-list-container.will-change-scroll(ref="scrollContainer")
    GameList.main-element(v-if="room", :class="{ 'navbar-exist': hasNavbar }", :menu="menu", :isLoadingNextPage="isLoadingMore", @isJackpot="isJackpot")
  .bg-linear-to-t.fixed.bottom-0.w-full(class="from-black/70 h-[4.8rem] landscape:hidden landscape:xl:block")
</template>

<script setup>
import debounce from 'lodash/debounce'
import { storeToRefs } from 'pinia'
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'

import { useI18n } from '#imports'
import Banner from '@/components/Banner.vue'
import GameList from '@/components/GameList.vue'
import SideFlat from '@/components/SideFlat.vue'
import { useActivity } from '@/composables/activity/useActivity'
import { useLocaleSetting } from '@/composables/core/useLocaleSetting'
import { useDragScrollContainer } from '@/composables/ui/useDragScrollContainer'
import { useInfiniteScroll } from '@/composables/ui/useInfiniteScroll'
import { useGameStore } from '@/stores/game/game'
import { useMainStore } from '@/stores/main'

const { locale } = useI18n()
const { getCdnUrl, getFrontCdnUrl } = useCdnAssets()
const { locale: defaultLocale } = useSite()
const { getFromActivity, shouldFilter } = useLocaleSetting()

const emit = defineEmits(['clickLogo'])

const sideMenu = useSideMenu()
const { menu, menuItemStack, historyStack, stopAutoUpdate: sideMenuStopAutoUpdate } = sideMenu

const game = useGame()
const { room, stopAutoUpdate: gameStopAutoUpdate } = game

const gameStore = useGameStore()
const { isLoadingMore, isLastPage } = storeToRefs(gameStore)

const { activityList, navbarSettings } = useActivity('main-component')

const mainStore = useMainStore()
const { setGameSearchPageEnable: setGameSearchPageEnableAction } = mainStore

const isInJackpot = ref(false)
const showMobileMenu = ref(null)

const { showCustomerService } = useCustomerServiceMenu()

const bannerList = computed(() => {
  return activityList.value.filter((campaign) => {
    if (shouldFilter(campaign)) {
      return getCdnUrl(getFromActivity(campaign, 'carouselUrl'))
    } else {
      return getCdnUrl(campaign.carouselUrl?.[0]?.url) || ''
    }
  })
})

const shouldShowBanner = computed(() => {
  return bannerList.value.length > 0 && !isInJackpot.value
})

const hasNavbar = computed(() => {
  return Array.isArray(navbarSettings.value) && navbarSettings.value.length > 0
})

const isJackpot = (bool) => {
  isInJackpot.value = bool
}

const { scrollContainer } = useInfiniteScroll({
  loadMore: () => game.nextPage(),
  isLastPage,
  isLoadingMore,
  threshold: 0.7,
  throttleDelay: 200,
  onError: (error) => {
    console.error('Failed to load next page:', error)
  },
})

useDragScrollContainer(scrollContainer)

const clickLogo = () => {
  emit('clickLogo')
}

watch(menuItemStack, (newData, oldData) => {
  if (newData.slice(-1)[0]?.categoryId !== oldData.slice(-1)[0]?.categoryId) {
    setGameSearchPageEnableAction(false)
  }
})

function updateShowMobileMenu() {
  const isLandscapeDesktop =
    document.body.clientWidth >= 1024 && (typeof window.matchMedia === 'function' ? window.matchMedia('(orientation: landscape)').matches : false)
  showMobileMenu.value = !isLandscapeDesktop
}

const debouncedUpdateShowMobileMenu = debounce(updateShowMobileMenu, 300)

onMounted(() => {
  updateShowMobileMenu()
  window.addEventListener('resize', debouncedUpdateShowMobileMenu)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', debouncedUpdateShowMobileMenu)
  debouncedUpdateShowMobileMenu.cancel()
})

onUnmounted(() => {
  sideMenuStopAutoUpdate()
  gameStopAutoUpdate()
})
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

main {
  padding-inline-start: env(safe-area-inset-left);
}

.main-element {
  height: calc(100vh - 7.75rem);
  max-height: 690px;
  @apply portrait:h-auto portrait:max-h-max;
}

.main-element.navbar-exist {
  height: calc(100vh - 8.5rem);
}

@media screen and (orientation: landscape) and (min-width: 1024px) {
  .main-element {
    max-height: none;
    height: calc(100vh - 9.75rem);
  }

  .main-element.navbar-exist {
    height: calc(100vh - 12.4797rem);
  }
}

.game-list-container {
  @apply cursor-grab active:cursor-grabbing pt-2 pb-11 portrait:h-full portrait:overflow-x-hidden portrait:overflow-y-auto portrait:pe-3;
}

@media screen and (orientation: landscape) and (max-width: 1023px) {
  .game-list-container {
    @apply py-2;
  }
}

.banner {
  width: calc((100vh - 7.75rem) * 44 / 69);
  max-width: 440px;
  max-height: 690px;
  align-self: center;
}

.banner.navbar-exist {
  width: calc((100vh - 9.75rem) * 44 / 69);
}

@media screen and (min-width: 1024px) {
  .banner {
    width: calc((100vh - 9.75rem) * 44 / 69);
  }

  .banner.navbar-exist {
    width: calc((100vh - 12.4797rem) * 44 / 69);
  }
}

@media (min-width: 1024px) and (orientation: landscape) {
  .side {
    display: none;
  }
}
</style>
